"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { CartIcon } from "@/components/shop/CartIcon";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Drawer, DrawerContent, DrawerClose } from "@/components/ui/drawer";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="h-3 w-20 bg-muted/40 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} aria-label={`Discover our ${item.label} essentials`} className="group relative font-default text-[14px] font-medium uppercase tracking-[0.15em] text-foreground hover:bg-transparent hover:text-foreground focus:bg-transparent focus:text-foreground px-0 py-2">
                <span className="relative inline-block">
                  {item.label}
                  <span className="pointer-events-none absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6">
        {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="h-8 w-48 bg-muted/40 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onClose} className="font-default text-[32px] leading-none tracking-tight text-foreground hover:text-primary transition-colors">
          {item.label}
        </Link>)}
    </nav>;
}
function ProductSearchResults({
  hasQuery,
  onResultClick
}: {
  hasQuery: boolean;
  onResultClick: () => void;
}) {
  const {
    products,
    loading
  } = useEcommerceProducts();
  if (!hasQuery) {
    return <div className="py-6 text-center font-default text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
        Type to search our collection
      </div>;
  }
  if (loading) {
    return <div className="space-y-3 py-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-12 w-full bg-muted/40 animate-pulse rounded-md" />)}
      </div>;
  }
  if (products.length === 0) {
    return <div className="py-6 text-center font-default text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
        No products found
      </div>;
  }
  return <ul className="max-h-80 space-y-1 overflow-y-auto py-1">
      {products.slice(0, 6).map((product, i) => <li key={product.id} data-index={i}>
          <Link to={product.link} onClick={onResultClick} className="flex items-center gap-3 rounded-lg p-2 hover:bg-accent/50 transition-colors">
            {product.featured_image_url && <img src={product.featured_image_url} alt={product.title} className="h-10 w-10 rounded-lg object-cover" loading="lazy" />}
            <div className="min-w-0 flex-1">
              <div className="truncate font-default text-sm text-foreground">
                {product.title}
              </div>
              {product.price !== undefined && <div className="font-default text-[11px] tracking-wide text-primary">
                  ${product.price}
                </div>}
            </div>
            <AddToCartButton productId={product.id} variant="ghost" size="sm" text="Add" showQuantity={false} buttonClassName="bg-primary text-white rounded-lg" />
          </Link>
        </li>)}
    </ul>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="350" id="header" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-300 ${scrolled ? "bg-background/80 backdrop-blur-md border-b border-border" : "border-b border-border"}`}>
      <div className="pointer-events-none absolute left-0 top-1/2 hidden -translate-x-full -translate-y-1/2 -rotate-90 origin-right pr-3 lg:block">
        <span className="font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground whitespace-nowrap">
          B.Y.E. Family Hygiene Mist™
        </span>
      </div>

      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 md:px-10">
        <div className="flex items-center">
          <Link to="/" className="flex items-center" aria-label="B.Y.E. Family Hygiene Mist home">
            <WvcLogo className="h-10 w-auto" />
          </Link>
        </div>

        <div className="hidden flex-1 justify-center md:flex">
          <MenuProvider menu_id="76">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex items-center gap-1 md:gap-4">
          <div className="flex items-center gap-1 md:gap-2">
            <Popover open={searchOpen} onOpenChange={setSearchOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Search our family-friendly products" className="text-foreground hover:bg-accent/40 hover:text-foreground">
                  <Search className="size-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" sideOffset={12} className="w-80 border-border bg-popover p-3 rounded-lg shadow-lg">
                <EcommerceProductsProvider wp_query={wp_query}>
                  <div className="space-y-3">
                    <div className="font-default text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      Search our collection
                    </div>
                    <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search mist, essentials, family solutions" />
                    <ProductSearchResults hasQuery={hasSearchQuery} onResultClick={() => {
                    setSearchOpen(false);
                    setSearchValue("");
                  }} />
                  </div>
                </EcommerceProductsProvider>
              </PopoverContent>
            </Popover>

            <CartIcon variant="ghost" size="icon" iconSize="size-4" className="text-foreground hover:bg-accent/40 hover:text-foreground" />
          </div>

          <Button asChild variant="default" className="hidden lg:flex rounded-lg px-6 font-default text-xs uppercase tracking-widest">
            <Link to="/ShopPage">Shop Now</Link>
          </Button>

          <Button variant="ghost" size="icon" aria-label="Open navigation menu" onClick={() => setMobileOpen(true)} className="md:hidden text-foreground hover:bg-accent/40 hover:text-foreground">
            <Menu className="size-5" />
          </Button>
        </div>
      </div>

      <SafeHydrate><Drawer open={mobileOpen} onOpenChange={setMobileOpen} direction="right">
        <DrawerContent className="!w-full !max-w-full bg-background text-foreground border-none">
          <div className="relative h-full w-full overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/mist_menu_backdrop.png)] opacity-10" />
            <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background" />

            <div className="relative z-10 flex h-full flex-col">
              <div className="flex h-20 items-center justify-between px-6 border-b border-border">
                <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center" aria-label="B.Y.E. Family Hygiene Mist home">
                  <WvcLogo className="h-10 w-auto" />
                </Link>
                <DrawerClose asChild>
                  <Button variant="ghost" size="icon" aria-label="Close navigation menu" className="text-foreground hover:bg-accent/40 hover:text-foreground">
                    <X className="size-5" />
                  </Button>
                </DrawerClose>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-10">
                <div className="mb-8 font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  Navigation
                </div>
                <MenuProvider menu_id="76">
                  <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                </MenuProvider>
              </div>

              <div className="border-t border-border px-6 py-6">
                <span className="font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  Before You Enter™ — Gentle Family Hygiene
                </span>
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer></SafeHydrate>
    </section>;
}